/**
 * 飞书 OAuth 回调凭证的一次性捕获。
 *
 * 必须是 main.ts 的第一条 import：在任何登录跳转、埋点或第三方脚本改写
 * 地址栏之前，把 code/state 收进模块内存，并立即从 URL 里抹掉——
 * code 一次性、5 分钟有效，不允许停留在 history / 日志 / 埋点里。
 *
 * sessionStorage 托管（2026-10-27 增补）：回调页不在 AUTH_WHITELIST，
 * Auth.checkLogin 在浏览器无有效登录态时会整页跳 SSO 一圈（登录或静默续期
 * 都是整页导航），回来时 SPA 重载、模块内存凭证随上个页面实例丢失，回调页
 * 只能报「授权信息已失效」（直接打开授权链接的实测事故）。捕获成功后把
 * 凭证短暂托管进 sessionStorage——tab 级隔离（不跨标签页、关 tab 即毁）、
 * 5 分钟过期对齐 state TTL、回调页消费即清；URL 无凭证的加载从这里恢复。
 * 不用 localStorage：跨标签页长存放大了暴露面，与 code 一次性语义不匹配。
 */
import { LARK_CALLBACK_PATH } from '@/views/assistant/datasource/feishu/oauth'

export interface LarkCallbackCredentials {
  code: string
  state: string
}

const HANDOVER_KEY = 'agui.lark.callback.handover'
/** 与后端 state TTL（5min）对齐；code 本身也是 5 分钟一次性 */
const HANDOVER_TTL_MS = 5 * 60 * 1000

interface HandoverPayload {
  code: string
  state: string
  /** 过期时间戳（ms）；超期视为无效，宁可让用户重新发起 */
  exp: number
}

function capture(): LarkCallbackCredentials | null {
  if (typeof window === 'undefined') return null

  const url = new URL(window.location.href)
  if (url.pathname !== LARK_CALLBACK_PATH) return null

  const code = url.searchParams.get('code')?.trim()
  const state = url.searchParams.get('state')?.trim()
  if (!code || !state) return null

  // 立即摘除凭证，保留其余 query 与 hash（路由/回跳由页面自行处理）
  const search = new URLSearchParams(url.search)
  search.delete('code')
  search.delete('state')
  const query = search.toString()
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${query ? `?${query}` : ''}${url.hash}`,
  )

  return { code, state }
}

function stashHandover(credentials: LarkCallbackCredentials): void {
  try {
    const payload: HandoverPayload = { ...credentials, exp: Date.now() + HANDOVER_TTL_MS }
    sessionStorage.setItem(HANDOVER_KEY, JSON.stringify(payload))
  } catch {
    // 隐私模式禁用 storage：降级为仅内存（回到旧行为，无法跨整页跳转存续）
  }
}

/** 从 sessionStorage 恢复托管凭证（不在此删除，消费时统一清理）；过期/损坏返回 null */
function restoreHandover(): LarkCallbackCredentials | null {
  try {
    const raw = sessionStorage.getItem(HANDOVER_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<HandoverPayload>
    if (!parsed.code || !parsed.state || typeof parsed.exp !== 'number') return null
    if (Date.now() > parsed.exp) {
      sessionStorage.removeItem(HANDOVER_KEY)
      return null
    }
    return { code: parsed.code, state: parsed.state }
  } catch {
    return null
  }
}

function clearHandover(): void {
  try {
    sessionStorage.removeItem(HANDOVER_KEY)
  } catch {
    // 与 stash 同源的降级：忽略
  }
}

const capturedFromUrl = capture()

let pending: LarkCallbackCredentials | null = capturedFromUrl

if (capturedFromUrl) {
  // 本次加载带着新凭证：即刻托管，整页跳转（SSO 登录/续期）回来后可恢复
  stashHandover(capturedFromUrl)
}

/**
 * 消费回调凭证：只有第一次返回，避免重复提交同一个 code；消费即清托管。
 *
 * 托管恢复必须放在这里惰性执行，不能在模块加载时按路径判断（2026-10-28
 * 回归修复）：SSO 登录回来后 SDK 用 history.replaceState 把路径改回回调页
 * ——那次页面加载的 pathname 是登录落地页而非回调页，按加载期路径判定会
 * 漏掉托管凭证，回调页随即误报「授权信息已失效」。惰性恢复既不依赖加载
 * 路径，又保持「凭证只在回调页进内存」的边界（本函数只有回调页调用）。
 */
export function consumeLarkCallback(): LarkCallbackCredentials | null {
  const credentials =
    pending ??
    (window.location.pathname === LARK_CALLBACK_PATH ? restoreHandover() : null)
  pending = null
  if (credentials) clearHandover()
  return credentials
}
